/**
 * Shared stacking / collision tokens for portaled "i" explain popovers.
 * One family: modal Root + Portal + translucent scrim + opaque Content
 * (opacity-100 + isolate). Scrim uses `--dialog-overlay-scrim` (true alpha)
 * so list / BN / drawer chrome stay dimmed-but-visible; panel stays opaque.
 */

export type InfoExplainCollisionPadding = {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
};

export const INFO_EXPLAIN_EDGE_PAD_PX = 12;

export const INFO_EXPLAIN_DEFAULT_COLLISION_PADDING: InfoExplainCollisionPadding =
  {
    top: INFO_EXPLAIN_EDGE_PAD_PX,
    right: INFO_EXPLAIN_EDGE_PAD_PX,
    bottom: INFO_EXPLAIN_EDGE_PAD_PX,
    left: INFO_EXPLAIN_EDGE_PAD_PX,
  };

/**
 * Full-viewport dismiss scrim under the panel (same z as content).
 * Must stay alpha-translucent (`--dialog-overlay-scrim` / `rgba(0,0,0,0.5)`) —
 * opaque color-mix blanks BN/list underlay in phone CDP evidence.
 */
export const INFO_EXPLAIN_SCRIM_CLASS =
  'fixed inset-0 z-[var(--z-popover)] bg-[var(--dialog-overlay-scrim)]';

/**
 * Opaque portaled panel stack — shared by MetricExplain / AnalyticsPanel /
 * EventMeasureHelp so callers stay in one stacking family.
 */
export const INFO_EXPLAIN_PORTAL_CONTENT_CLASS =
  'z-[var(--z-popover)] border border-[var(--color-an-border)] bg-[var(--color-an-surface)] text-[var(--color-an-text)] opacity-100 isolate outline-none';

/** MetricExplain scroll-capped dialog panel (sentence-case, elevation-3). */
export const INFO_EXPLAIN_METRIC_PANEL_CLASS = `${INFO_EXPLAIN_PORTAL_CONTENT_CLASS} flex max-h-[min(70dvh,calc(100dvh-var(--admin-bottom-chrome,0px)-var(--admin-test-economy-chrome-height,0px)-1.5rem))] w-[min(22.5rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-xl text-sm normal-case shadow-[var(--elevation-3,var(--elevation-2))] [text-transform:none]`;

/** Compact analytics / measure help panel. */
export const INFO_EXPLAIN_COMPACT_PANEL_CLASS = `${INFO_EXPLAIN_PORTAL_CONTENT_CLASS} max-h-[min(70dvh,calc(100dvh-var(--admin-bottom-chrome,0px)-var(--admin-test-economy-chrome-height,0px)-1.5rem))] overflow-y-auto overscroll-contain rounded-xl shadow-[var(--elevation-2)] normal-case [text-transform:none]`;

export function readCssPxVar(name: string): number {
  if (typeof window === 'undefined') {
    return 0;
  }
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Resolve a CSS length (incl. `var(--safe-area-inset-*)` / `env()`) to px.
 * Custom properties that wrap `env()` stay unresolved via getPropertyValue —
 * a probe element forces computation.
 */
export function readCssLengthPx(cssLength: string): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0;
  }
  const probe = document.createElement('div');
  probe.setAttribute('data-info-explain-collision-probe', '');
  probe.style.cssText = [
    'position:absolute',
    'visibility:hidden',
    'pointer-events:none',
    'top:0',
    'left:0',
    'width:0',
    'height:0',
    'margin:0',
    'border:0',
    `padding-top:${cssLength}`,
  ].join(';');
  document.documentElement.appendChild(probe);
  const px = Number.parseFloat(window.getComputedStyle(probe).paddingTop);
  probe.remove();
  return Number.isFinite(px) ? px : 0;
}

function readSafeAreaInset(
  side: 'top' | 'right' | 'bottom' | 'left',
): number {
  // Concrete px on :root (tests / overrides) — getPropertyValue parses these.
  const fromRoot = readCssPxVar(`--safe-area-inset-${side}`);
  if (fromRoot > 0) {
    return fromRoot;
  }
  // design-tokens wrap env(); unresolved `env(...)` needs a probe to compute.
  return readCssLengthPx(
    `var(--safe-area-inset-${side}, env(safe-area-inset-${side}, 0px))`,
  );
}

/**
 * Collision padding for Radix Popover Content — clears TEST chrome, bottom nav,
 * keyboard inset, and safe-area (notch / home indicator) on top + bottom.
 */
export function readChromeCollisionPadding(): InfoExplainCollisionPadding {
  return {
    top:
      INFO_EXPLAIN_EDGE_PAD_PX +
      readCssPxVar('--admin-test-economy-chrome-height') +
      readSafeAreaInset('top'),
    right: INFO_EXPLAIN_EDGE_PAD_PX + readSafeAreaInset('right'),
    bottom:
      INFO_EXPLAIN_EDGE_PAD_PX +
      readCssPxVar('--admin-bottom-chrome') +
      readCssPxVar('--keyboard-inset') +
      readSafeAreaInset('bottom'),
    left: INFO_EXPLAIN_EDGE_PAD_PX + readSafeAreaInset('left'),
  };
}